import React, { useState, useRef, useEffect } from 'react';
import { API, COLORS } from '../utils/constants';
import { r1, detectarTipoComida } from '../utils/helpers';
import MaximizedPopup from './MaximizedPopup';
import Popup from './Popup';

const GEO_ENABLED = false;

// Limpia entrada decimal preservando "0." al inicio (mismo helper que AlimentoWizard)
const cleanDecimal = (v) => {
  if (!v) return '';
  let cleaned = v.replace(/[^0-9.,]/g, '');
  if (cleaned.match(/^0+[.,]/)) cleaned = cleaned.replace(/^0+/, '0');
  cleaned = cleaned.replace(',', '.');
  const parts = cleaned.split('.');
  if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
  return cleaned;
};

/**
 * Modal reutilizable para añadir/editar alimentos.
 *
 * Props:
 *   open          – boolean
 *   onClose       – () => void
 *   nutrition     – estado de nutrición actual
 *   setNutrition  – setter
 *   onToast       – () => void  (el padre muestra el toast)
 *   editingFood   – { index, food } | null
 *   onEditDone    – () => void  (llamado tras guardar edición)
 *   seed          – datos iniciales opcionales cuando viene del wizard/scan
 *                   { input, baseNutrients, baseUnidadMode, tipoComida,
 *                     wizardAlimentoExtra, pendingImagePath, step }
 */
export default function AddFoodModal({
  open,
  onClose,
  nutrition,
  setNutrition,
  onToast,
  editingFood = null,
  onEditDone = null,
  seed = null,
}) {
  const [addStep, setAddStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [confirmSaveDb, setConfirmSaveDb] = useState(false);
  const [input, setInput] = useState({ name: '', calories: '', protein: '', carbs: '', fat: '', grams: '' });
  const [tipoComida, setTipoComida] = useState('sin clasificar');
  const [tipoComidaFijo, setTipoComidaFijo] = useState(false); // true cuando viene del + de sección
  const [suggestions, setSuggestions] = useState([]);
  const [baseNutrients, setBaseNutrients] = useState(null);
  const [baseUnidadMode, setBaseUnidadMode] = useState(null);
  const [wizardAlimentoExtra, setWizardAlimentoExtra] = useState(null);
  const [pendingImagePath, setPendingImagePath] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const searchTimeout = useRef(null);
  const suggestionsRef = useRef(null);

  // Reset scroll to top when suggestions appear
  useEffect(() => {
    if (suggestionsRef.current) {
      suggestionsRef.current.scrollTop = 0;
    }
  }, [suggestions.length]);

  useEffect(() => {
    if (suggestions.length > 0) {
      requestAnimationFrame(() => {
        if (!suggestionsRef.current) return;
        const container = document.querySelector('[data-maxpopup="true"]');
        if (!container) return;
        const elRect = suggestionsRef.current.getBoundingClientRect();
        const contRect = container.getBoundingClientRect();
        if (elRect.bottom > contRect.bottom - 8) {
          container.scrollTop += (elRect.bottom - contRect.bottom) + 16;
        }
      });
    }
  }, [suggestions.length]);

  const highlightMatch = (text, query) => {
    if (!query || query.length < 2) return <span style={{ color: COLORS.text }}>{text}</span>;
    const lowerText = text.toLowerCase();
    const idx = lowerText.indexOf(query.toLowerCase());
    if (idx === -1) return <span style={{ color: COLORS.text }}>{text}</span>;
    const wordStart = lowerText.lastIndexOf(' ', idx - 1) + 1;
    let wordEnd = lowerText.indexOf(' ', idx + query.length);
    if (wordEnd === -1) wordEnd = text.length;
    const beforeWord = text.slice(0, wordStart);
    const word = text.slice(wordStart, wordEnd);
    const afterWord = text.slice(wordEnd);
    const mIdx = idx - wordStart;
    return (
      <>
        {beforeWord && <span style={{ color: COLORS.text }}>{beforeWord}</span>}
        <span style={{ color: COLORS.green }}>
          {word.slice(0, mIdx)}
          <strong>{word.slice(mIdx, mIdx + query.length)}</strong>
          {word.slice(mIdx + query.length)}
        </span>
        {afterWord && <span style={{ color: COLORS.text }}>{afterWord}</span>}
      </>
    );
  };

  // Inicializar estado cuando se abre el modal.
  // Intencionalmente sólo se ejecuta cuando `open` cambia;
  // editingFood y seed se leen como snapshot en ese momento.
  useEffect(() => {
    if (!open) return;
    if (editingFood) {
      const f = editingFood.food;
      const isUnidad = f.tipo_medida_usada === 'unidad';
      const cantUsada = isUnidad ? (Number(f.cantidad_usada) || 1) : 1;
      const pesoUni = Number(f.peso_unidad) || 100;
      const gramoTotales = isUnidad ? (cantUsada * pesoUni) : (Number(f.grams) || 100);
      setInput({ name: f.name, calories: String(f.calories), protein: String(f.protein), carbs: String(f.carbs), fat: String(f.fat), grams: isUnidad ? String(cantUsada) : String(f.grams || 0) });
      setBaseNutrients({
        calories: f.calories / (gramoTotales || 100) * 100,
        carbs: f.carbs / (gramoTotales || 100) * 100,
        fat: f.fat / (gramoTotales || 100) * 100,
        protein: f.protein / (gramoTotales || 100) * 100,
        pesoPorUnidad: pesoUni,
        esPorUnidad: isUnidad,
      });
      setBaseUnidadMode(isUnidad ? { tipo: 'unidad', label: 'u', pesoXUnidad: pesoUni } : null);
      setAddStep(2);
    } else if (seed) {
      if (seed.input) setInput(seed.input);
      if (seed.baseNutrients) setBaseNutrients(seed.baseNutrients);
      setBaseUnidadMode(seed.baseUnidadMode ?? null);
      if (seed.tipoComida) setTipoComida(seed.tipoComida);
      setTipoComidaFijo(!!seed.forceTipoComida);
      setWizardAlimentoExtra(seed.wizardAlimentoExtra ?? null);
      setPendingImagePath(seed.pendingImagePath ?? null);
      setAddStep(seed.step ?? 1);
    } else {
      setTipoComidaFijo(false);
      setInput({ name: '', calories: '', protein: '', carbs: '', fat: '', grams: '' });
      setBaseNutrients(null);
      setBaseUnidadMode(null);
      setWizardAlimentoExtra(null);
      setPendingImagePath(null);
      setSuggestions([]);
      setAddStep(1);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetAndClose = () => {
    setSuggestions([]);
    onClose();
  };

  const searchFood = async (query) => {
    if (query.length < 2) { setSuggestions([]); setSearchError(null); return; }
    setSearching(true);
    setSearchError(null);
    try {
      const results = await fetch(API + `/alimentos/buscar?q=${encodeURIComponent(query)}`, { credentials: 'include' }).then(r => r.json()).catch(() => []);
      setSuggestions((results || []).slice(0, 12));
    } catch { 
      setSuggestions([]); 
    } finally {
      setSearching(false);
    }
  };

  const selectSuggestion = (s) => {
    setBaseNutrients({ calories: s.calorias, protein: s.proteina, carbs: s.carbos, fat: s.grasa });
    const tm = s.tipo_medida || s.tipo_cantidad || 'peso';
    if (tm === 'liquido' && s.densidad && s.densidad !== 1.0) {
      const dens = s.densidad;
      setBaseUnidadMode({ tipo: 'liquido', label: 'ml', pesoXUnidad: dens, densidad: dens });
      setInput({ id: s.id, es_receta: s.es_receta, name: s.nombre, calories: String(r1(s.calorias * dens)), protein: String(r1(s.proteina * dens)), carbs: String(r1(s.carbos * dens)), fat: String(r1(s.grasa * dens)), grams: '100' });
    } else if ((tm === 'unidad' || s.tipo_cantidad === 'unidad' || s.tipo_cantidad === 'porcion') && s.peso_unidad) {
      const pw = s.peso_unidad;
      const lbl = s.nombre_unidad || (s.tipo_cantidad === 'porcion' ? 'porciones' : 'unidades');
      setBaseUnidadMode({ tipo: 'unidad', label: lbl, pesoXUnidad: pw, densidad: 1.0 });
      setInput({ id: s.id, es_receta: s.es_receta, name: s.nombre, calories: String(r1(s.calorias * pw / 100)), protein: String(r1(s.proteina * pw / 100)), carbs: String(r1(s.carbos * pw / 100)), fat: String(r1(s.grasa * pw / 100)), grams: '1' });
    } else {
      setBaseUnidadMode(null);
      setInput({ id: s.id, es_receta: s.es_receta, name: s.nombre, calories: String(s.calorias), protein: String(s.proteina), carbs: String(s.carbos), fat: String(s.grasa), grams: '100' });
    }
    setSuggestions([]);
  };

  const handleGramsChange = (g) => {
    if (g.length > 1 && g.startsWith('0') && !g.startsWith('0.')) g = g.replace(/^0+/, '');
    const units = parseFloat(g) || 0;
    let gr;
    if (baseUnidadMode) {
      if (baseUnidadMode.tipo === 'liquido') gr = units * (baseUnidadMode.densidad || 1.0);
      else gr = units * baseUnidadMode.pesoXUnidad;
    } else { gr = units; }
    if (baseNutrients && gr > 0) {
      setInput(p => ({
        ...p,
        grams: g,
        calories: ((baseNutrients.calories * gr) / 100).toFixed(1),
        protein: ((baseNutrients.protein * gr) / 100).toFixed(1),
        carbs: ((baseNutrients.carbs * gr) / 100).toFixed(1),
        fat: ((baseNutrients.fat * gr) / 100).toFixed(1),
      }));
    } else {
      setInput(p => ({ ...p, grams: g }));
    }
  };

  const addFood = async () => {
    setSaving(true);
    const today = new Date().toISOString().split('T')[0];
    const hora = new Date().toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
    let latitud = null, longitud = null;
    if (GEO_ENABLED) {
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 }));
        latitud = pos.coords.latitude;
        longitud = pos.coords.longitude;
      } catch {}
    }
    let actualGrams;
    if (baseUnidadMode) {
      if (baseUnidadMode.tipo === 'liquido') actualGrams = Math.round(Number(input.grams || 0) * (baseUnidadMode.densidad || 1.0) * 10) / 10;
      else actualGrams = Math.round(Number(input.grams || 0) * baseUnidadMode.pesoXUnidad * 10) / 10;
    } else { actualGrams = Number(input.grams || 0); }
    const entry = { name: input.name, calories: Number(input.calories || 0), protein: Number(input.protein || 0), carbs: Number(input.carbs || 0), fat: Number(input.fat || 0), grams: actualGrams, time: hora, tipo_comida: tipoComida };
    
    const todayStr = new Date().toISOString().split('T')[0];
    const targetDate = seed?.targetDate || todayStr;
    const isToday = targetDate === todayStr;

    if (isToday) {
      const updated = {
        calories: { ...nutrition.calories, current: nutrition.calories.current + entry.calories },
        protein: { ...nutrition.protein, current: nutrition.protein.current + entry.protein },
        carbs: { ...nutrition.carbs, current: nutrition.carbs.current + entry.carbs },
        fat: { ...nutrition.fat, current: nutrition.fat.current + entry.fat },
        foods: [...(nutrition.foods || []), entry],
      };
      setNutrition(updated);
    }
    
    // Retroactividad de vínculos para el registro diario
    const tmu = baseUnidadMode ? baseUnidadMode.tipo : "peso";
    
    await fetch(API + '/food-log', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ fecha: targetDate, nombre: entry.name, calorias: entry.calories, proteina: entry.protein, carbos: entry.carbs, grasa: entry.fat, gramos: entry.grams, hora, tipo_comida: tipoComida, latitud, longitud, image_path: pendingImagePath, referencia_id: input.id || null, es_receta: input.es_receta || false, cantidad_usada: Number(input.grams||0), tipo_medida_usada: tmu }),
    }).catch(() => {});
    // Solo guardar en /alimentos si NO es una receta (las recetas ya están en su propia tabla)
    if (!input.es_receta) {
      await fetch(API + '/alimentos', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ nombre: input.name, calorias: Number(input.calories || 0), proteina: Number(input.protein || 0), carbos: Number(input.carbs || 0), grasa: Number(input.fat || 0), ...(wizardAlimentoExtra || {}) }),
      }).catch(() => {});
    }
    setSaving(false);
    onToast();
    resetAndClose();
  };

  const saveEdit = async () => {
    setSaving(true);
    const todayStr = new Date().toISOString().split('T')[0];
    const targetDate = editingFood?.date || todayStr;
    const isToday = targetDate === todayStr;
    
    const updated = {
      name: input.name,
      calories: Number(input.calories || 0),
      protein: Number(input.protein || 0),
      carbs: Number(input.carbs || 0),
      fat: Number(input.fat || 0),
      grams: Number(input.grams || 0),
      time: editingFood.food.time,
      id: editingFood.food.id,
    };
    if (editingFood.food.id) {
      await fetch(API + `/food-log/${editingFood.food.id}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
      await fetch(API + '/food-log', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ fecha: targetDate, nombre: updated.name, calorias: updated.calories, proteina: updated.protein, carbos: updated.carbs, grasa: updated.fat, gramos: updated.grams, hora: updated.time, tipo_comida: editingFood.food.tipo_comida || detectarTipoComida(updated.name) }),
      }).catch(() => {});
    }
    if (isToday) {
      const newFoods = nutrition.foods.map((f, i) => i === editingFood.index ? updated : f);
      const newNutrition = {
        calories: { ...nutrition.calories, current: newFoods.reduce((s, x) => s + x.calories, 0) },
        protein: { ...nutrition.protein, current: newFoods.reduce((s, x) => s + x.protein, 0) },
        carbs: { ...nutrition.carbs, current: newFoods.reduce((s, x) => s + x.carbs, 0) },
        fat: { ...nutrition.fat, current: newFoods.reduce((s, x) => s + x.fat, 0) },
        foods: newFoods,
      };
      setNutrition(newNutrition);
    }
    setSaving(false);
    if (onEditDone) onEditDone();
    onToast();
    resetAndClose();
  };

  if (!open && !confirmData && !confirmSaveDb) return null;

  return (
    <>
    {open && (
    <MaximizedPopup onBackdropClick={resetAndClose}>
      <div style={{ padding: '24px 20px 44px' }}>
        {/* PASO 1: Búsqueda */}
        {addStep === 1 && !editingFood && (
          <>
            {/* Header sticky con input siempre visible */}
            <div style={{ position: 'sticky', top: 0, zIndex: 100, background: '#1a1a26', padding: '24px 20px 16px', margin: '-24px -20px 0', borderRadius: '20px 20px 0 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ color: COLORS.text, fontSize: 18, fontWeight: 800 }}>¿Qué has comido?</span>
                <button onClick={resetAndClose} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>✕</button>
              </div>
              <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>NOMBRE DEL ALIMENTO</label>
              <input
                autoFocus
                type="text"
                value={input.name}
                onChange={e => {
                  setInput(p => ({ ...p, name: e.target.value }));
                  clearTimeout(searchTimeout.current);
                  searchTimeout.current = setTimeout(() => searchFood(e.target.value), 400);
                }}
                placeholder="Busca un alimento..."
                style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '12px 14px', color: COLORS.text, fontSize: 16, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            {/* Sugerencias - primera siempre visible debajo del input */}
            {(suggestions.length > 0 || input.name.length >= 2) && (
              <div ref={suggestionsRef} style={{ background: '#1a1a26', border: `1px solid ${COLORS.border}`, borderRadius: 10, overflowY: 'auto', maxHeight: '50vh', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: 8 }}>
                {suggestions.map((s, i) => (
                  <div
                    key={i}
                    onClick={() => { selectSuggestion(s); if (!tipoComidaFijo) setTipoComida(detectarTipoComida(s.nombre)); setAddStep(2); }}
                    style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{highlightMatch(s.nombre, input.name)}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>{r1(s.calorias)} kcal · {r1(s.proteina)}g prot · por 100g</div>
                    </div>
                    <span style={{ color: COLORS.green, fontSize: 10, fontWeight: 700, marginLeft: 8 }}>›</span>
                  </div>
                ))}
                {input.name.length >= 2 && (
                  <button
                      disabled={searching}
                      onClick={async () => {
                        if (searching) return;
                        setSearching(true);
                        setSearchError(null);
                        try {
                          // 1. Intentar OpenFoodFacts (sin credentials para evitar CORS issues si no es necesario)
                          const res = await fetch(API + `/alimentos/online?q=${encodeURIComponent(input.name)}`).then(r => r.json()).catch(() => []);
                          
                          const first = Array.isArray(res) && res.length > 0 ? res[0] : null;
                          if (first && first.calorias) {
                            setConfirmData({ nombre: first.nombre || input.name, calorias: first.calorias, proteina: first.proteina || 0, carbos: first.carbos || 0, grasa: first.grasa || 0, fuente: 'openfoodfacts' });
                          } else {
                            // 2. Fallback a Claude
                            const claudeRes = await fetch(API + '/alimentos/buscar-nombre', { 
                              method: 'POST', 
                              headers: {'Content-Type': 'application/json'}, 
                              body: JSON.stringify({ nombre: input.name }) 
                            });
                            
                            if (!claudeRes.ok) throw new Error("No se pudo obtener información");
                            
                            const claude = await claudeRes.json();
                            if (claude && claude.calorias !== undefined) {
                              setConfirmData({ nombre: claude.nombre || input.name, calorias: claude.calorias, proteina: claude.proteina || 0, carbos: claude.carbos || 0, grasa: claude.grasa || 0, fuente: 'claude', cantidad_sugerida: claude.cantidad_sugerida });
                            } else {
                              setSearchError("No se encontraron datos para este alimento.");
                            }
                          }
                        } catch (err) { 
                          console.error(err);
                          setSearchError("Ocurrió un error al buscar en internet.");
                        } finally { 
                          setSearching(false); 
                        }
                      }}
                      style={{ width: '100%', border: 'none', background: 'transparent', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, color: COLORS.blue, fontSize: 13, fontWeight: 700, cursor: searching ? 'default' : 'pointer', borderTop: `1px solid ${COLORS.border}` }}
                    >
                      <span style={{ fontSize: 18, filter: searching ? 'grayscale(1)' : 'none' }}>{searching ? '⏳' : '🌐'}</span>
                      <span style={{ flex: 1, textAlign: 'left' }}>{searching ? 'Buscando información...' : `Buscar "${input.name}" en internet`}</span>
                      {!searching && <span style={{ opacity: 0.5 }}>›</span>}
                    </button>
                  )}
                  {searchError && (
                    <div style={{ padding: '10px 16px', color: '#ff453a', fontSize: 12, fontWeight: 600, background: 'rgba(255,69,58,0.1)', borderTop: `1px solid ${COLORS.border}` }}>
                      ⚠️ {searchError}
                    </div>
                  )}
              </div>
            )}
          </>
        )}

        {/* PASO 2: Cantidad (o edición) */}
        {(addStep === 2 || editingFood) && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>CANTIDAD DE</div>
                <span style={{ color: COLORS.text, fontSize: 18, fontWeight: 800 }}>{input.name}</span>
              </div>
              {!editingFood
                ? <button onClick={() => { setAddStep(1); setSuggestions([]); }} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>← Volver</button>
                : <button onClick={resetAndClose} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>✕</button>
              }
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>CANTIDAD</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  autoFocus
                  type="text"
                  inputMode="decimal"
                  value={input.grams || ''}
                  onChange={e => handleGramsChange(cleanDecimal(e.target.value))}
                  placeholder="0"
                  style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '14px 16px', paddingRight: 72, color: COLORS.text, fontSize: 22, fontWeight: 700, outline: 'none', boxSizing: 'border-box' }}
                />
                <span style={{ position: 'absolute', right: 14, color: COLORS.textMuted, fontSize: 13, fontWeight: 600, pointerEvents: 'none' }}>
                  {!baseUnidadMode ? 'gramos' : baseUnidadMode.tipo === 'liquido' ? 'ml' : (baseUnidadMode.label || 'unidades')}
                </span>
              </div>
              {input.calories && (
                <div style={{ marginTop: 10, background: COLORS.bg, borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: COLORS.accent, fontWeight: 800, fontSize: 16 }}>{r1(input.calories)} kcal</span>
                  <span style={{ color: COLORS.textMuted, fontSize: 12 }}>{r1(input.protein)}g prot · {r1(input.carbs)}g carb · {r1(input.fat)}g gras</span>
                </div>
              )}
            </div>
            {seed?.fromPhoto && !editingFood && (
              <button
                onClick={async () => {
                  if (!seed?.baseNutrients) return;
                  const bn = seed.baseNutrients;
                  await fetch(API + '/alimentos', { method: 'POST', headers: {'Content-Type': 'application/json'}, credentials: 'include', body: JSON.stringify({ nombre: input.name, calorias: bn.calories, proteina: bn.protein, carbos: bn.carbs, grasa: bn.fat, fuente: 'foto', tipo_medida: 'peso', tipo_cantidad: 'peso' }) }).catch(() => {});
                  onToast('✅ Guardado en base de datos');
                }}
                style={{ width: '100%', padding: 14, borderRadius: 12, background: 'rgba(255,255,255,0.06)', border: `1px solid rgba(255,255,255,0.12)`, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14, marginBottom: 8 }}
              >
                📚 Guardar en base de datos
              </button>
            )}
            <button
              disabled={saving}
              onClick={editingFood ? saveEdit : addFood}
              style={{ width: '100%', padding: 16, borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: 'none', color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: 800, fontSize: 16 }}
            >
              {saving ? '⏳' : editingFood ? 'ACTUALIZAR' : 'Añadir al recuento'}
            </button>
          </>
        )}
      </div>
    </MaximizedPopup>
    )}
    {confirmData && (
      <Popup onClose={() => { setConfirmData(null); setConfirmSaveDb(false); }} title={confirmSaveDb ? "Guardar en Base Central" : "Confirmar alimento"} zIndex={10001}>
          {[["Nombre", "nombre", "text"], ["Calorías 100g", "calorias", "number"], ["Proteína 100g", "proteina", "number"], ["Carbos 100g", "carbos", "number"], ["Grasa 100g", "grasa", "number"]].map(([label, key, type]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 4 }}>{label.toUpperCase()}</label>
              <input type="text" inputMode={type === "number" ? "decimal" : "text"} value={confirmData[key] ?? ""} onChange={e => setConfirmData(p => ({ ...p, [key]: type === "number" ? cleanDecimal(e.target.value) : e.target.value }))} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
            </div>
          ))}
          <div style={{ marginBottom: 14 }}>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 4 }}>¿CÓMO SE MIDE ESTE ALIMENTO?</label>
            <select value={confirmData.tipo_medida || "peso"} onChange={e => setConfirmData(p => ({ ...p, tipo_medida: e.target.value }))} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", color: COLORS.text, fontSize: 15, outline: "none" }}>
              <option value="peso">Gramos</option>
              <option value="unidad">Por Unidades (ej: 1 manzana, 1 rebanada)</option>
              <option value="liquido">Mililitros</option>
            </select>
          </div>
          {confirmData.tipo_medida === 'unidad' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 4 }}>PESO DE 1 UNIDAD (GR)</label>
              <input type="text" inputMode="decimal" value={confirmData.peso_unidad ?? ""} onChange={e => setConfirmData(p => ({ ...p, peso_unidad: cleanDecimal(e.target.value) }))} placeholder="Ej: 150" style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
            </div>
          )}

          {!confirmSaveDb ? (
            <>
              <button onClick={async () => {
                const d = confirmData;
                const tm = d.tipo_medida || 'peso';
                const pu = parseFloat(d.peso_unidad) || null;
                setConfirmData(null);
                selectSuggestion({ nombre: d.nombre, calorias: parseFloat(d.calorias)||0, proteina: parseFloat(d.proteina)||0, carbos: parseFloat(d.carbos)||0, grasa: parseFloat(d.grasa)||0, tipo_medida: tm, tipo_cantidad: tm, peso_unidad: pu });
                if (!tipoComidaFijo) setTipoComida(detectarTipoComida(d.nombre));
                setAddStep(2);
                if (d.cantidad_sugerida) {
                   setTimeout(() => handleGramsChange(String(d.cantidad_sugerida)), 100);
                }
              }} style={{ width: "100%", padding: 14, borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", marginBottom: 10 }}>AÑADIR AL DIARIO</button>
              <button onClick={() => setConfirmSaveDb(true)} style={{ width: "100%", padding: 12, borderRadius: 12, background: "rgba(10,132,255,0.15)", border: "1px solid rgba(10,132,255,0.4)", color: "#0a84ff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>📚 GUARDAR EN BASE GLOBAL</button>
            </>
          ) : (
            <>
              <button onClick={async () => {
                const d = confirmData;
                const tm = d.tipo_medida || 'peso';
                const pu = parseFloat(d.peso_unidad) || null;
                await fetch(API + '/alimentos', { method: 'POST', headers: {'Content-Type': 'application/json'}, credentials: 'include', body: JSON.stringify({ nombre: d.nombre, calorias: parseFloat(d.calorias)||0, proteina: parseFloat(d.proteina)||0, carbos: parseFloat(d.carbos)||0, grasa: parseFloat(d.grasa)||0, fuente: d.fuente || 'online', tipo_medida: tm, tipo_cantidad: tm, peso_unidad: pu }) }).catch(() => {});
                onToast && onToast('✅ Guardado en base de datos global');
                setConfirmSaveDb(false);
              }} style={{ width: "100%", padding: 14, borderRadius: 12, background: "rgba(10,132,255,0.2)", border: "1px solid rgba(10,132,255,0.5)", color: "#0a84ff", fontSize: 15, fontWeight: 800, cursor: "pointer", marginBottom: 10 }}>CONFIRMAR GUARDADO DB</button>
              <button onClick={() => setConfirmSaveDb(false)} style={{ width: "100%", padding: 12, borderRadius: 12, background: "transparent", border: "1px solid " + COLORS.border, color: COLORS.textMuted, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>VOLVER ATRÁS</button>
            </>
          )}
      </Popup>
    )}
    </>
  );
}
